import { ServiceCardTypeItems } from "../services/service.card.types";

export type FilterType = "range" | "checkbox" | "rating" | "map";

export type FilterOption = {
  key: string;
  value: string;
};

export type FilterConfig = {
  id: string;
  type: FilterType;
  label: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  options?: FilterOption[];
  min?: number;
  max?: number;
  step?: number;
  currency?: string;
  displayType?: "currency" | "duration";
};

export const SORT_OPTION_VALUES = [
  "default",
  "highest_price",
  "lowest_price",
  "top_review",
  "highest_rating",
] as const;

export type SortOption = (typeof SORT_OPTION_VALUES)[number];

export const isSortOption = (
  value: string | null | undefined,
): value is SortOption =>
  typeof value === "string" &&
  (SORT_OPTION_VALUES as readonly string[]).includes(value);

export const TAB_OPTION_VALUES = [
  "popular",
  "near_me",
  "near_city",
  "best_deal",
] as const;

export type TabOption = (typeof TAB_OPTION_VALUES)[number];

export const isTabOption = (
  value: string | null | undefined,
): value is TabOption =>
  typeof value === "string" &&
  (TAB_OPTION_VALUES as readonly string[]).includes(value);

export type CanonicalServiceType = "hotel" | "rental" | "spa_msg" | "tour_act";
export type ServiceType = CanonicalServiceType | "hotels" | "rentals";

export const normalizeServiceType = (
  service: ServiceType | string,
): CanonicalServiceType => {
  if (service === "hotels" || service === "hotel") return "hotel";
  if (service === "rentals" || service === "rental") return "rental";
  if (service === "spa_msg") return "spa_msg";
  if (service === "tour_act") return "tour_act";
  return "hotel";
};

export type ResultLayout = "oneColumn" | "twoColumn" | "threeColumn";

export type ResultCardAttribute = {
  id?: number;
  name: string;
  type?: string;
  iconName?: string;
  data: string;
};

/**
 * Currently not use in search reasult page yet
 * this type using in hook, so it's will update later
 */
export type ResultCardData = {
  id: string;
  image: string;
  title: string;
  lat?: number;
  lng?: number;
  rating?: number;
  reviewCount?: number;
  specs?: string[];
  attributes?: ResultCardAttribute[];
  price?: number;
  salePrice?: number;
  disPercent?: number;
  hasSalePrice?: boolean;
  distanceFormatted?: string;
  locationText?: string;
  href?: string;
  [key: string]: unknown;
};


/**
 * Mapper from search to card 
 * Hotel, Rental, Spa and Activity
 */
export type HotelSearchResult = {
  id: number | string;
  title: string;
  starRating: number;
  price: number;
  salePrice: number
  disPercent: number;
  availMsg: string;
  totalBooking: number;
  isFavorite: boolean;
  hasBreakfast: boolean;
  isInstantConfirm: boolean;
  cancelPolicy: {
    id?: number | null;
    message?: string | null;
  } | null;
  rating: number;
  reviewCount: number;
  specs: string[];
  distance: string;
  distanceFormatted: string;
  attributes: ResultCardAttribute[];
  image: string | null;
  [key: string]: unknown;
};

export type RentalSearchResult = {
  id: string | number;
  title: string;
  price: number;
  salePrice: number;
  disPercent?: number;
  discount?: number;
  rating: number;
  reviewCount: number;
  isFavorite: boolean;
  isInstantConfirm: boolean;
  specs: string[];
  distance: string;
  image: string;
  quickIcons?: ServiceCardTypeItems["quickIcons"];
  [key: string]: unknown;
};


export type SpaSearchResult = {
  id: number | string;
  title: string;
  feature: string;
  salePrice: number;
  price: number;
  disPercent: number;
  serviceName: string;
  duration: string;
  distance: number;
  distanceUnit: string;
  distanceMeasurement: string;
  merchant?: {
    avgRating: number;
    totalReview: number;
  } | null;
  isFavorite: boolean;
  isInstantConfirm: boolean;
};


export type TourSearchResult = {
  id: number | string;
  title: string;
  feature: string;
  salePrice: number;
  price: number;
  disPercent: number;
  category?: {
    id: number;
    name: string;
  } | null;
  duration: string | number | null;
  distance: number;
  distanceUnit: string;
  distanceMeasurement: string;
  merchant?: {
    avgRating: number;
    totalReview: number;
  } | null;
  isFavorite: boolean;
  isInstantConfirm: boolean;
};


export type EmptyStateTuple = readonly [title: string, description: string];
export const SERVICE_EMPTY_STATE_TUPLES: Record<CanonicalServiceType, EmptyStateTuple> = {
  hotel: [
    "No Stays Found",
    "No hotels available for your selected dates. Please adjust your dates or explore other hotels.",
  ],
  rental: [
    "No Rentals Found",
    "No vehicles available for your selected dates. Please adjust your dates or explore other vehicles.",
  ],
  spa_msg: [
    "No Spas Found",
    "No spas or massage packages available. Please adjust your filters or explore other services.",
  ],
  tour_act: [
    "No Activities Found",
    "No tours or activities available for your search. Please adjust your filters or search for another destination.",
  ],
};